import { Injectable } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { Microservices } from '../../microservices';
import { Notification } from 'src/app/@types/Notification.type';
import { AuthService } from '../back-services/auth.service';
import { v4 as uuid } from 'uuid';
import { NotificationsDictionary } from "../../@lang/notifications-dictionary";
@Injectable({
  providedIn: 'root'
})
export class NotificationsService {
  notifications: Subject<Notification> = new Subject();

  private _notifications: Notification[] = [];
  private listeners: Subscription[] = [];

  private queue: { skip: number, limit: number }[] = [];
  private queuePromise?: Promise<void>;
  private _listenerId: number = 0;
  private uuidNotifications: Set<string> = new Set();
  private refInterval: any;
  private uuid = uuid()
  constructor(
    public micro: Microservices,
    private authService: AuthService
  ) {

  }

  setupListeners() {
    this.listeners.push(
      this.authService.token.onClear(() => this.clear.bind(this)),
      this.authService.token.onValue(token => this.setupNotificationsFeed(token)),

      this.notifications.subscribe(notification => {
        console.log('incoming notification', notification);
        this._notifications.unshift(notification)
      }),

      this.authService.token.onClear(() => this.clear.bind(this))
    );

    if (this.refInterval) clearInterval(this.refInterval);
    this.refInterval = setInterval(() => this.getNotificationsManually(), 2000)
  }

  removeListeners() {
    this.listeners.forEach(unlisten => unlisten.unsubscribe());
    this.listeners = [];
    if (this.refInterval) {
      clearInterval(this.refInterval);
      this.refInterval = null;
    }
  }

  async getUnreadNotifications() {
    const notifs = await this.getNotifications();
    const count = notifs.filter(notif => !notif.seen).length;

    return count;
  }

  getNotificationStream(): Observable<any> {
    return this.notifications;
  }

  async getNotifications({ limit, skip }: { limit: number, skip: number } = { limit: 50, skip: 0 }) {
    this.queue.push({ limit, skip });

    await this.processQueue();

    return await this.loadNotifications({ limit, skip });
  }

  async processQueue() {
    if (this.queue.length === 0) {
      delete this.queuePromise;
      return;
    }

    if (!this.queuePromise) {
      this.queuePromise = new Promise(async (resolve) => {
        while (this.queue.length > 0) {
          const { limit, skip } = this.queue.shift();

          await this.loadNotifications({ limit, skip });
        }
        resolve();
      });
    }

    await this.queuePromise;
    this.processQueue();
  }

  async loadNotifications({ limit, skip }: { limit: number, skip: number } = { limit: 50, skip: 0 }) {
    const lastIndex = limit + skip;
    let isAnythingMissing = false;

    for (let i = skip; i < lastIndex; i++) {
      if (!this._notifications[i]) {
        isAnythingMissing = true;
        break;
      }
    }

    if (!isAnythingMissing) {
      return this._notifications.slice(skip, lastIndex);
    }

    const notifications = await this.micro.Obs.Notifications.notifications(localStorage.token, {
      limit, skip
    });


    for (let i = 0; i < notifications.length; i++) {
      this._notifications[skip + i] = notifications[i];
      if (notifications[i].uuid) this.uuidNotifications.add(notifications[i].uuid);
    }

    return this._notifications.slice(skip, lastIndex);
  }

  async getNotificationsManually() {
    try {
      const token = await this.authService.token.get();
      if (!token) return;

      const pollingNotifications = await this.micro.Obs.Notifications.unsyncNotifications(this.uuid, token);

      for (const notification of pollingNotifications) {
        if (notification.uuid && this.uuidNotifications.has(notification.uuid)) continue;

        if (notification.uuid) this.uuidNotifications.add(notification.uuid);
        this._notifications.unshift(notification);
        this.notifications.next(notification);
      }
    } catch (error) {
      console.log(error)
    }
  }

  notifySeen() {
    return this.micro.Obs.Notifications.notifySeen(localStorage.token);
  }

  notifyRead(notification) {
    return this.micro.Obs.Notifications.notifyRead(notification._id, localStorage.token);
  }

  translate(name, data = {}) {
    let msg = (NotificationsDictionary)[name];
    for (const fieldName in data) {
      msg = msg.replace(new RegExp(`\\{\\{${fieldName}\\}\\}`, "g"), data[fieldName]);
    }
    return msg;
  }

  translateWithMessage(name, data = {}, message) {
    const dictionary = NotificationsDictionary;
    if (!dictionary) return message;

    let msg = dictionary[name];
    let namedData = dictionary.$variables;

    if (msg && msg !== "") {
      for (const fieldName in data) {
        let ndata = namedData && namedData[fieldName] || {};
        msg = msg.replace(new RegExp(`\\{\\{${fieldName}\\}\\}`, "g"), ndata[data[fieldName]] || data[fieldName]);
      }
      return msg;
    } else {
      return message;
    }
  }

  async clear() {
    try {
      this._notifications = [];
      this.clearNotificationsFeed();
      this.micro.Obs.Notifications.unsubscribe(this.uuid, localStorage.token);
    } catch (error) {
      console.log(error)
    }
  }

  setupNotificationsFeed(token: string) {
    this.micro.Obs.Notifications.subscribe(this.uuid, token, (notification: Notification) => {
      if (notification.uuid && this.uuidNotifications.has(notification.uuid)) return;

      if (notification.uuid) this.uuidNotifications.add(notification.uuid);
      this.notifications.next(notification);
    });

  }

  clearNotificationsFeed() {

  }
}
